import React, {useEffect, useState, useRef} from 'react';
import cx from 'classnames';
import moment from 'moment';

import PageContainer from '../../js/components/PageContainer';
import DocumentHeader from '../../js/components/DocumentHeader';
import Button, {ButtonTypes} from '../../js/components/ButtonNew';
import {Article} from '../../js/components/LinkedData';
import Link from '../../js/components/Link';
import CourseCard from '../../js/components/CourseCard';
import BlogPostCard from '../../js/components/BlogPostCard';
import QuestionCard from '../../js/components/QuestionCard';
import ServiceCard from '../../js/components/ServiceCard';
import BlogCoachCard from '../../js/components/BlogCoachCard';
import SignupBox from '../../js/components/SignupBox';
import TableOfContents, {generateTOC} from '../../js/components/TableOfContents';
import {useAnalytics, useAppContext} from '../../js/hooks';
import transformHTML from '../../js/util/htmlFormatter';

import {getPostBySlug, getBlogRelatedContent} from '../../../server/services/blog';

const Grid = (props) => <div className="grid grid-cols-1 gap-2 md:grid-cols-2 md:gap-4" {...props} />;

// Not using db tags here because tags on ghost are not in sync with db tags
const SlugToRole = {
  'data-science': 'data science',
  em: 'engineering management',
  bizops: 'bizops',
  'ml-engineer': 'machine learning',
  devops: 'devops',
  'product-designer': 'product designer',
  pm: 'product management',
  pmm: 'product marketing',
  'solutions-architect': 'solutions architect',
  swe: 'software engineering',
  'system-design': 'system design',
  tpm: 'technical program management',
};

const SignupCard = ({post}) => {
  const {tracker} = useAnalytics();

  return (
    <div className="mt-5 rounded-xl bg-gradient-to-r from-indigo-700 to-purple-700 shadow-xl">
      <div className="py-4 px-4">
        <h3 className="text-lg font-semibold mb-1 leading-tight tracking-tight text-white">
          <span className="block">Everything you need to know for your next interview</span>
        </h3>
        <p className="text-sm text-gray-100 font-regular mb-3">
          Create your free Exponent account and learn how to ace your interviews.
        </p>
        <Button
          href="/signup?src=blog"
          buttonType={ButtonTypes.SECONDARY}
          className="w-full text-indigo-700"
          onClick={() => tracker.blogClickSignupCta({post: post.slug})}
        >
          {'Get started free ->'}
        </Button>
      </div>
    </div>
  );
};

export default function Page({post, relatedContent}) {
  const [toc, setTOC] = useState();
  const {currentUser} = useAppContext();
  const {tracker} = useAnalytics();
  const contentRef = useRef(null);
  // A post can have multiple tags, but we want the best tag to use for displaying related content
  // getBlogRelatedContent selects that tag for us
  const tag = relatedContent?.tag;

  const transformedHTML = transformHTML(post.html);

  useEffect(() => {
    setTOC(generateTOC());
  }, [post]);

  useEffect(() => {
    tracker.blogViewPost({slug: post.slug});
  }, [post?.slug]);

  useEffect(() => {
    const links = contentRef.current.querySelectorAll('a');
    links.forEach((link) => {
      link.setAttribute('target', '_blank');
    });
  }, []);

  let roleName;
  post.tags?.forEach(({slug}) => {
    if (SlugToRole[slug]) roleName = SlugToRole[slug];
  });

  return (
    <PageContainer stickyHeader metadata={{tag}} exitEnabled={false} scrollEnabled>
      <div className="bg-white w-full mx-auto relative">
        {/* Metadata */}
        <DocumentHeader
          title={post.title}
          socialDescription={post.meta_description || post.custom_excerpt}
          imgPath={post.feature_image}
        />
        <Article
          headline={post.title}
          image={post.feature_image}
          datePublished={post.published_at}
          dateModified={post.updated_at}
        />

        {/* Post header */}
        <div className="mx-auto max-w-7xl px-4 lg:px-0 gap-8 relative grid grid-cols-10 lg:border-b border-gray-200 lg:pb-8">
          <div className="col-span-10 lg:col-span-6 lg:col-start-3">
            <div className="flex flex-col items-center">
              <h1 className="mt-10 md:mt-16 mb-6 text-3xl md:text-4xl font-bold text-black text-center" style={{fontFamily: 'Sora'}}>
                {post.title}
              </h1>
              {post.primary_tag && (
                <Link
                  href={`/blog/tag/${post.primary_tag.slug}`}
                  className="rounded-full bg-gray-50 px-3 py-1.5 font-medium text-gray-600 hover:bg-gray-100"
                >
                  {post.primary_tag.name}
                </Link>
              )}
            </div>
            <div className="my-4 mb-10 flex items-center text-center justify-center">
              {post.primary_author.profile_image && (
                <img
                  src={post.primary_author.profile_image}
                  alt={post.primary_author.name}
                  className="mr-2 w-6 h-6 rounded-full object-cover"
                />
              )}
              <span className="text-sm font-medium text-gray-700">
                {post.primary_author.name}
                {' • '}
                {post.updated_at !== post.published_at ? 'Last updated' : 'Published'}
                {' '}
                <time dateTime={post.updated_at}>
                  {moment(post.updated_at).fromNow()}
                </time>
              </span>
            </div>
            <img src={post.feature_image} alt="" className="rounded-lg" />
          </div>
        </div>

        {/* Post body and table of contents */}
        <div className="mt-2 max-w-7xl px-4 lg:px-0 mx-auto gap-8 relative grid grid-cols-10">
          <div className="hidden lg:block col-span-2">
            {toc?.length > 0 && (
              <div className="sticky top-20">
                <TableOfContents sections={toc} />
              </div>
            )}
          </div>


          <div className={cx('w-full mx-auto col-span-10 lg:col-span-6')}>
            {toc?.length > 0 && (
              <div className="lg:hidden">
                <TableOfContents sections={toc} mobile />
              </div>
            )}
            <div
              ref={contentRef}
              className="mx-auto mt-8 prose lg:prose-lg prose-indigo"
              dangerouslySetInnerHTML={{__html: transformedHTML}}
            />
          </div>

          {/* Sidebar CTA cards */}
          <div className="hidden lg:block col-span-2 pr-4">
            <div className="sticky top-20 pt-2">
              {tag?.type === 'service' && (
                <ServiceCard
                  tag={tag}
                  trackerEvent={() => tracker.blogClickCard({type: 'service', tag: tag.id})}
                  urlParams={{src: 'blog'}}
                />
              )}
              {!currentUser
              && !relatedContent?.questionsExist
              && !relatedContent?.coachesExist && (
                <SignupCard post={post} />
              )}
              {relatedContent?.questionsExist && (
                <div className="mb-4">
                  <QuestionCard
                    tag={tag}
                    trackerEvent={() => tracker.blogClickCard({type: 'questions', tag: tag.id})}
                    urlParams={{src: 'blog'}}
                  />
                </div>
              )}
              {relatedContent?.coachesExist && (
                <div>
                  <BlogCoachCard
                    tag={tag}
                    trackerEvent={() => tracker.blogClickCard({type: 'coaching', tag: tag.id})}
                    urlParams={{src: 'blog'}}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Example add-on feature */}
        {!currentUser ? (
          <div className="container max-w-2xl">
            <SignupBox
              role={roleName}
              href="/signup?src=blog"
              onClick={() => tracker.blogClickSignupCta({post: post.slug})}
            />
          </div>
        ) : (
          <div className="h-12" /> // Spacer
        )}

        {/* Related Courses */}
        {relatedContent?.courses?.length > 0 && (
          <div className="mx-auto max-w-3xl px-4 pb-10">
            <h3 className="mb-5 text-2xl font-bold text-black">Related Courses</h3>
            <Grid>
              {relatedContent.courses
                .map((course) => (
                  <CourseCard
                    course={course}
                    trackerEvent={() => tracker.blogClickCard({type: 'courses', tag: tag.id, slug: course.slug})}
                    urlParams={{src: 'blog'}}
                  />
                ))}
            </Grid>
          </div>
        )}

        {/* Related Blog Posts */}
        {relatedContent?.blogPosts?.length > 0 && (
        <div className="mx-auto max-w-3xl px-4 pb-10">
          <h3 className="mb-5 text-2xl font-bold text-black">Related Blog Posts</h3>
          <Grid>
            {relatedContent.blogPosts.map((p) => (
              <BlogPostCard
                post={p}
                trackerEvent={() => tracker.blogClickCard({type: 'blog', tag: tag.id, slug: p.slug})}
                urlParams={{src: 'blog'}}
              />
            ))}
          </Grid>
        </div>
        )}
      </div>
    </PageContainer>
  );
}

Page.getLayout = (page) => page;

export async function getStaticProps({params}) {
  const post = await getPostBySlug(params.slug);

  if (!post) return {notFound: true};

  const relatedContent = await getBlogRelatedContent(post) || {};

  return {
    props: {post, relatedContent},
    revalidate: 60,
  };
}

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: 'blocking',
  };
}
